/**
 * Copyright © 2026 Delicious AI, LLC
 */

import { ApolloError, useLazyQuery, useMutation } from '@apollo/client'
import type {
  UltraNextBoundingBoxesForClassificationQuery,
  UltraNextBoundingBoxesForClassificationQueryVariables,
  UltraReleaseBoundingBoxLeasesMutation,
  UltraReleaseBoundingBoxLeasesMutationVariables,
  UltraRevertClassificationBatchMutation,
  UltraRevertClassificationBatchMutationVariables,
  UltraSubmitClassificationBatchMutation,
  UltraSubmitClassificationBatchMutationVariables,
} from '@dai/graphql'
import {
  NEXT_BOUNDING_BOXES_FOR_CLASSIFICATION,
  RELEASE_BOUNDING_BOX_LEASES,
  REVERT_CLASSIFICATION_BATCH,
  SUBMIT_CLASSIFICATION_BATCH,
} from '@dai/graphql'
import React, { useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'

import useT from '@/i18n/useT'
import type {
  ClassificationBatch,
  LeaseLockError,
  NextBoundingBoxesForClassificationResult,
  SubmitClassificationBatchInput,
} from './classificationTypes'

import {
  buildClassificationSubmission,
  REVIEW_DURATION_MS,
} from './classificationUtils'

const getBatchKey = (batch: ClassificationBatch | null) =>
  batch
    ? `${batch.upc}:${batch.boundingBoxes.map(box => box.id).join(',')}`
    : ''

const AUTO_FILLED_UPC_STORAGE_KEY = 'classification:autoFilledUpc'

export const formatClassificationRetryAfter = (
  retryAfterMs: number | null,
  t?: ReturnType<typeof useT>['t'],
) =>
  retryAfterMs
    ? `${Math.ceil(retryAfterMs / 1000)}s`
    : t
      ? t('classification.retryAfter.moment', 'a moment')
      : 'a moment'

type ClassificationWorkflowOptions = {
  manageLeases?: boolean
  manageInitialBatchLease?: boolean
  onSubmitSuccess?: () => Promise<unknown> | unknown
}

export const useClassificationWorkflow = (
  initialBatch: ClassificationBatch | null = null,
  {
    manageLeases = true,
    manageInitialBatchLease = manageLeases,
    onSubmitSuccess,
  }: ClassificationWorkflowOptions = {},
) => {
  const { t } = useT()
  const [searchParams, setSearchParams] = useSearchParams()
  const [selectedPreviewBoxId, setSelectedPreviewBoxId] = React.useState<
    string | null
  >(null)
  const [visibleBoxIds, setVisibleBoxIds] = React.useState<string[]>([])
  const [rejectedIds, setRejectedIds] = React.useState<Set<string>>(new Set())
  const [reviewStartedAt, setReviewStartedAt] = React.useState<number | null>(
    null,
  )
  const [now, setNow] = React.useState(() => Date.now())
  const [undoPayload, setUndoPayload] =
    React.useState<SubmitClassificationBatchInput | null>(null)
  const [snackbarOpen, setSnackbarOpen] = React.useState(false)
  const [undoConfirmOpen, setUndoConfirmOpen] = React.useState(false)
  const [injectedBatch, setInjectedBatch] =
    React.useState<ClassificationBatch | null>(initialBatch)
  const currentBatchKeyRef = React.useRef('')
  const initialBatchKeyRef = React.useRef(getBatchKey(initialBatch))
  const initializedBatchKeyRef = React.useRef<string | null>(null)
  const handledBatchKeysRef = React.useRef(new Set<string>())
  const latestRequestIdRef = React.useRef(0)
  const skipNextSearchParamLoadRef = React.useRef(false)
  const requestedUpc = searchParams.get('upc')?.trim() || null
  const [result, setResult] =
    React.useState<NextBoundingBoxesForClassificationResult | null>(null)
  const [loading, setLoading] = React.useState(false)
  const [error, setError] = React.useState<ApolloError | null>(null)
  const [autoFilledUpc, setAutoFilledUpc] = React.useState<string | null>(
    () => {
      if (typeof window === 'undefined') return null
      return window.sessionStorage.getItem(AUTO_FILLED_UPC_STORAGE_KEY)
    },
  )
  const isAutoFilledUpc =
    Boolean(requestedUpc) && requestedUpc === autoFilledUpc

  const [loadBatch] = useLazyQuery<
    UltraNextBoundingBoxesForClassificationQuery,
    UltraNextBoundingBoxesForClassificationQueryVariables
  >(NEXT_BOUNDING_BOXES_FOR_CLASSIFICATION, {
    context: { endPoint: 'mlUltra' },
    fetchPolicy: 'network-only',
  })

  const [submitClassificationBatch, { loading: submitting }] = useMutation<
    UltraSubmitClassificationBatchMutation,
    UltraSubmitClassificationBatchMutationVariables
  >(SUBMIT_CLASSIFICATION_BATCH, {
    context: { endPoint: 'mlUltra' },
  })

  const [revertClassificationBatch] = useMutation<
    UltraRevertClassificationBatchMutation,
    UltraRevertClassificationBatchMutationVariables
  >(REVERT_CLASSIFICATION_BATCH, {
    context: { endPoint: 'mlUltra' },
  })

  const [releaseBoundingBoxLeases] = useMutation<
    UltraReleaseBoundingBoxLeasesMutation,
    UltraReleaseBoundingBoxLeasesMutationVariables
  >(RELEASE_BOUNDING_BOX_LEASES, {
    context: { endPoint: 'mlUltra' },
  })

  const fetchedBatch =
    result?.__typename === 'ClassificationBatch'
      ? (result as ClassificationBatch)
      : null
  const sourceBatch = fetchedBatch ?? injectedBatch
  const leaseLock =
    result?.__typename === 'LeaseLockError' ? (result as LeaseLockError) : null
  const noItems = result?.__typename === 'NoItemsAvailableError' ? result : null

  const batch = React.useMemo(() => {
    if (!sourceBatch) return null
    const allowedIds = new Set(visibleBoxIds)

    return {
      ...sourceBatch,
      boundingBoxes: sourceBatch.boundingBoxes.filter(
        box => !box.isFlagged && allowedIds.has(box.id),
      ),
    }
  }, [sourceBatch, visibleBoxIds])

  const syncSearchParams = React.useCallback(
    (upc?: string | null) => {
      const next = new URLSearchParams(searchParams)

      if (upc !== undefined) {
        if (upc) next.set('upc', upc)
        else next.delete('upc')
      }

      if (next.toString() !== searchParams.toString()) {
        setSearchParams(next, { replace: true })
      }
    },
    [searchParams, setSearchParams],
  )

  const requestBatch = React.useCallback(
    async (upc: string | null) => {
      const requestId = latestRequestIdRef.current + 1
      latestRequestIdRef.current = requestId
      setLoading(true)
      setError(null)

      try {
        const response = await loadBatch({ variables: { upc } })
        if (latestRequestIdRef.current !== requestId) {
          return null
        }

        const nextResult =
          response.data?.nextBoundingBoxesForClassification ?? null
        setResult(nextResult)
        setLoading(false)
        return nextResult
      } catch (loadError) {
        if (latestRequestIdRef.current !== requestId) {
          return null
        }

        setResult(null)
        setError(loadError as ApolloError)
        setLoading(false)
        throw loadError
      }
    },
    [loadBatch],
  )

  useEffect(() => {
    latestRequestIdRef.current += 1
    setResult(null)
    setError(null)
    setLoading(false)
    setInjectedBatch(initialBatch)
    initialBatchKeyRef.current = getBatchKey(initialBatch)
  }, [initialBatch])

  useEffect(() => {
    if (initialBatch) {
      return
    }
    if (skipNextSearchParamLoadRef.current) {
      skipNextSearchParamLoadRef.current = false
      return
    }
    void requestBatch(requestedUpc)
  }, [initialBatch, requestBatch, requestedUpc])

  useEffect(() => {
    if (
      !initialBatch &&
      fetchedBatch?.upc &&
      !fetchedBatch.requiresAnchorReplacement
    ) {
      if (requestedUpc === fetchedBatch.upc) {
        return
      }

      skipNextSearchParamLoadRef.current = true
      if (typeof window !== 'undefined') {
        if (!requestedUpc || isAutoFilledUpc) {
          window.sessionStorage.setItem(
            AUTO_FILLED_UPC_STORAGE_KEY,
            fetchedBatch.upc,
          )
        } else {
          window.sessionStorage.removeItem(AUTO_FILLED_UPC_STORAGE_KEY)
        }
      }

      setAutoFilledUpc(
        !requestedUpc || isAutoFilledUpc ? fetchedBatch.upc : null,
      )
      syncSearchParams(fetchedBatch.upc)
    }
  }, [
    fetchedBatch?.requiresAnchorReplacement,
    fetchedBatch?.upc,
    initialBatch,
    isAutoFilledUpc,
    requestedUpc,
    syncSearchParams,
  ])

  useEffect(() => {
    if (!requestedUpc && autoFilledUpc) {
      if (typeof window !== 'undefined') {
        window.sessionStorage.removeItem(AUTO_FILLED_UPC_STORAGE_KEY)
      }
      setAutoFilledUpc(null)
      return
    }

    if (requestedUpc && autoFilledUpc && requestedUpc !== autoFilledUpc) {
      if (typeof window !== 'undefined') {
        window.sessionStorage.removeItem(AUTO_FILLED_UPC_STORAGE_KEY)
      }
      setAutoFilledUpc(null)
    }
  }, [autoFilledUpc, requestedUpc])

  useEffect(() => {
    if (!reviewStartedAt) return

    const interval = window.setInterval(() => {
      setNow(Date.now())
    }, 250)

    return () => window.clearInterval(interval)
  }, [reviewStartedAt])

  const releaseBatch = React.useCallback(
    async (boxIds: string[], batchKey?: string) => {
      if (!manageLeases) return
      if (!manageInitialBatchLease && batchKey === initialBatchKeyRef.current) {
        return
      }
      if (!boxIds.length) return
      try {
        await releaseBoundingBoxLeases({ variables: { boxIds } })
      } catch (releaseError) {
        console.error('Failed to release classification leases', releaseError)
      }
    },
    [manageInitialBatchLease, manageLeases, releaseBoundingBoxLeases],
  )

  useEffect(() => {
    const nextKey = getBatchKey(sourceBatch)
    const previousKey = currentBatchKeyRef.current

    if (
      previousKey &&
      previousKey !== nextKey &&
      !handledBatchKeysRef.current.has(previousKey)
    ) {
      const previousIds =
        previousKey.split(':')[1]?.split(',').filter(Boolean) || []
      void releaseBatch(previousIds, previousKey)
    }

    currentBatchKeyRef.current = nextKey
    if (initializedBatchKeyRef.current === nextKey) {
      return
    }

    initializedBatchKeyRef.current = nextKey
    setVisibleBoxIds(
      sourceBatch?.boundingBoxes
        .filter(box => !box.isFlagged)
        .map(box => box.id) ?? [],
    )
    setRejectedIds(new Set(sourceBatch?.rejectedBoxIds ?? []))
    setReviewStartedAt(sourceBatch ? Date.now() : null)
    setSelectedPreviewBoxId(null)
    setNow(Date.now())
  }, [releaseBatch, sourceBatch])

  useEffect(
    () => () => {
      const activeKey = currentBatchKeyRef.current
      if (!activeKey || handledBatchKeysRef.current.has(activeKey)) return
      const boxIds = activeKey.split(':')[1]?.split(',').filter(Boolean) || []
      void releaseBatch(boxIds, activeKey)
    },
    [releaseBatch],
  )

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setSelectedPreviewBoxId(null)
      }
      if ((event.metaKey || event.ctrlKey) && event.key === 'Enter') {
        document.getElementById('classification-submit')?.click()
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [])

  const reviewRemainingMs = Math.max(
    REVIEW_DURATION_MS - (reviewStartedAt ? now - reviewStartedAt : 0),
    0,
  )
  const actionableBoxes = React.useMemo(
    () => batch?.boundingBoxes ?? [],
    [batch],
  )
  const canSubmit =
    actionableBoxes.length > 0 && reviewRemainingMs === 0 && !submitting
  const rejectedCount = rejectedIds.size
  const acceptedCount = actionableBoxes.length - rejectedCount

  const handleRefresh = async () => {
    if (injectedBatch) {
      setInjectedBatch(null)
    }
    await requestBatch(requestedUpc)
  }

  const handleToggleRejected = React.useCallback((boxId: string) => {
    setRejectedIds(current => {
      const next = new Set(current)
      if (next.has(boxId)) next.delete(boxId)
      else next.add(boxId)
      return next
    })
  }, [])

  const handleSubmit = async (replacementUpc?: string | null) => {
    if (!batch || !canSubmit) return

    const activeBoxUpcs = new Set(
      actionableBoxes
        .map(box => ('currentUpc' in box ? box.currentUpc : null))
        .filter((upc): upc is string => Boolean(upc)),
    )
    const submitUpc =
      batch.activeUpc ??
      (activeBoxUpcs.size === 1 ? Array.from(activeBoxUpcs)[0] : batch.upc)
    const effectiveReplacementUpc =
      replacementUpc ??
      (batch.classificationReviewBatchId && submitUpc !== batch.upc
        ? batch.upc
        : null)
    const input = buildClassificationSubmission(
      submitUpc,
      actionableBoxes,
      rejectedIds,
      effectiveReplacementUpc,
      batch.classificationReviewBatchId,
    )

    handledBatchKeysRef.current.add(getBatchKey(sourceBatch))

    try {
      const response = await submitClassificationBatch({ variables: { input } })
      const payload = response.data?.submitClassificationBatch
      const action =
        payload?.__typename === 'SubmitClassificationBatchResult'
          ? payload.undoAction
          : null
      if (action?.variablesPayload) {
        try {
          setUndoPayload(
            JSON.parse(
              action.variablesPayload,
            ) as SubmitClassificationBatchInput,
          )
          setSnackbarOpen(true)
        } catch (parseError) {
          console.error('Failed to parse undo payload', parseError)
        }
      }
      setInjectedBatch(null)
      const nextUpc = initialBatch || !isAutoFilledUpc ? requestedUpc : null
      if (!initialBatch && isAutoFilledUpc) {
        skipNextSearchParamLoadRef.current = true
        if (typeof window !== 'undefined') {
          window.sessionStorage.removeItem(AUTO_FILLED_UPC_STORAGE_KEY)
        }
        setAutoFilledUpc(null)
        syncSearchParams(null)
      }
      if (onSubmitSuccess) {
        await onSubmitSuccess()
        return
      }
      await requestBatch(nextUpc)
    } catch (submitError) {
      handledBatchKeysRef.current.delete(getBatchKey(sourceBatch))
      console.error('Failed to submit classification batch', submitError)
    }
  }

  const removeVisibleBoxes = React.useCallback(
    (boxIds: string[]) => {
      if (!boxIds.length) return visibleBoxIds
      const removedIds = new Set(boxIds)
      const nextVisibleBoxIds = visibleBoxIds.filter(
        boxId => !removedIds.has(boxId),
      )
      setVisibleBoxIds(nextVisibleBoxIds)
      setRejectedIds(current => {
        const next = new Set(current)
        boxIds.forEach(boxId => next.delete(boxId))
        return next
      })
      setSelectedPreviewBoxId(current =>
        current && removedIds.has(current) ? null : current,
      )
      return nextVisibleBoxIds
    },
    [visibleBoxIds],
  )

  const handleCropFlagged = React.useCallback(
    async (boxId: string) => {
      const nextVisibleBoxIds = removeVisibleBoxes([boxId])
      if (nextVisibleBoxIds.length > 0) {
        return
      }

      try {
        setInjectedBatch(null)
        skipNextSearchParamLoadRef.current = true
        if (typeof window !== 'undefined') {
          window.sessionStorage.removeItem(AUTO_FILLED_UPC_STORAGE_KEY)
        }
        setAutoFilledUpc(null)
        syncSearchParams(null)
        await requestBatch(null)
      } catch (loadError) {
        console.error('Failed to reload classification batch', loadError)
        throw loadError
      }
    },
    [removeVisibleBoxes, requestBatch, syncSearchParams],
  )

  const handleBatchFlagged = React.useCallback(
    async (boxIds: string[]) => {
      if (!boxIds.length) return
      removeVisibleBoxes(boxIds)
      const batchKey = getBatchKey(sourceBatch)
      if (sourceBatch) {
        handledBatchKeysRef.current.add(batchKey)
      }
      try {
        setInjectedBatch(null)
        skipNextSearchParamLoadRef.current = true
        if (typeof window !== 'undefined') {
          window.sessionStorage.removeItem(AUTO_FILLED_UPC_STORAGE_KEY)
        }
        setAutoFilledUpc(null)
        syncSearchParams(null)
        await requestBatch(null)
      } catch (loadError) {
        if (sourceBatch) {
          handledBatchKeysRef.current.delete(batchKey)
        }
        console.error('Failed to reload classification batch', loadError)
        throw loadError
      }
    },
    [removeVisibleBoxes, requestBatch, sourceBatch, syncSearchParams],
  )

  const handleUndo = async () => {
    if (!undoPayload) return
    try {
      await revertClassificationBatch({ variables: { input: undoPayload } })
      setSnackbarOpen(false)
      setUndoConfirmOpen(true)
      await requestBatch(requestedUpc)
    } catch (undoError) {
      console.error('Failed to undo classification batch', undoError)
    }
  }

  const closeSnackbar = () => setSnackbarOpen(false)
  const closeUndoConfirm = () => setUndoConfirmOpen(false)

  const selectedPreviewBox =
    batch?.boundingBoxes.find(box => box.id === selectedPreviewBoxId) || null

  return {
    acceptedCount,
    batch,
    canSubmit,
    closeSnackbar,
    closeUndoConfirm,
    error,
    handleBatchFlagged,
    handleCropFlagged,
    handleRefresh,
    handleSubmit,
    handleToggleRejected,
    handleUndo,
    leaseLock,
    loading,
    noItems,
    rejectedCount,
    rejectedIds,
    result,
    reviewRemainingMs,
    selectedPreviewBox,
    selectedPreviewBoxId,
    setSelectedPreviewBoxId,
    snackbarOpen,
    undoConfirmOpen,
    visibleBoxes: actionableBoxes,
  }
}
